import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://dash.zorbastays.com"}/web-backend`;

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const calcNights = (checkin, checkout) => {
  if (!checkin || !checkout) return 0;
  const diff = new Date(checkout) - new Date(checkin);
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
};

// Helper to parse currency strings safely
const parseAmount = (val) => {
  if (!val) return 0;
  const num = parseFloat(String(val).replace(/,/g, ""));
  return isNaN(num) ? 0 : num;
};

const SP = { xs: 8, sm: 12, md: 16, lg: 24, xl: 32, xxl: 40 };

// ─── Sub-components ─────────────────────────────────────────────────────────

const Divider = () => (
  <hr
    style={{
      height: 3,
      border: "none",
      backgroundColor: "#1E2827",
      margin: `${SP.xl}px 0`,
    }}
  />
);

const SectionTitle = ({ children }) => (
  <h2
    style={{
      fontSize: 18,
      fontWeight: "bold",
      margin: `0 0 ${SP.md}px 0`,
      letterSpacing: 1,
      color: "#111",
    }}
  >
    {children}
  </h2>
);

// Return an array of <tr>s — safest pattern inside <tbody> during print.
const DetailRow = ({ label, value, subText }) => {
  const rows = [
    <tr key="main">
      <td
        style={{
          padding: `${SP.xs}px 0`,
          fontWeight: "bold",
          width: "50%",
          fontSize: 14,
          color: "#111",
          verticalAlign: "top",
        }}
      >
        {label}
      </td>
      <td
        style={{
          padding: `${SP.xs}px 0`,
          width: "50%",
          textAlign: "right",
          fontSize: 14,
          color: "#111",
          verticalAlign: "top",
        }}
      >
        {value}
      </td>
    </tr>,
  ];
  if (subText) {
    rows.push(
      <tr key="sub">
        <td
          colSpan={2}
          style={{
            padding: `0 0 ${SP.xs}px 0`,
            fontSize: 12,
            color: "#555",
            verticalAlign: "top",
          }}
        >
          {subText}
        </td>
      </tr>,
    );
  }
  return rows;
};
const COVER_W = 480; // fixed width  (px)
const COVER_H = 320; // fixed height (px)  → 3:2 box, always the same

const CoverImage = ({ src, fallback, onDone }) => {
  const [current, setCurrent] = useState(src || fallback || null);
  const [usedFallback, setUsedFallback] = useState(!src);

  useEffect(() => {
    setCurrent(src || fallback || null);
    setUsedFallback(!src);
    if (!src && !fallback) onDone?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, fallback]);

  if (!current) return null;

  return (
    // Fixed frame, centered on the page
    <div
      style={{
        width: COVER_W,
        height: COVER_H,
        maxWidth: "100%",
        margin: `${SP.lg}px 0`,
        // margin: `${SP.lg}px auto`,
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "#d6d5d0",
      }}
    >
      <img
        src={current}
        alt=""
        onLoad={() => onDone?.()}
        onError={() => {
          if (!usedFallback && fallback && fallback !== current) {
            setCurrent(fallback);
            setUsedFallback(true);
          } else {
            setCurrent(null);
            onDone?.();
          }
        }}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover", // fill the frame, crop the extra
          objectPosition: "center", // keep the center, crop the rest
        }}
      />
    </div>
  );
};
// ─── Main Component ─────────────────────────────────────────────────────────

const PreLeadPDF = ({ onClose, onPrint }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const autoprint = searchParams.get("autoprint") === "1";
  const [qrReady, setQrReady] = useState(false);
  const [coverDone, setCoverDone] = useState(false);
  useEffect(() => {
    const img = new Image();
    img.onload = () => setQrReady(true);
    img.onerror = () => setQrReady(true);
    img.src =
      "https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public";
  }, []);

  useEffect(() => {
    if (lead && !lead.cover_image && !lead.cover_fallback) setCoverDone(true);
  }, [lead]);
  useEffect(() => {
    if (!loading && lead && autoprint && qrReady && coverDone) {
      const t = setTimeout(() => {
        const firstname = (lead.firstname || lead.customer_name || "Lead")
          .trim()
          .replace(/\s+/g, "_");
        const leadId = lead.id || id;
        document.title = `${firstname}_${leadId}_prebooking.pdf`;
        window.print();
      }, 800);
      return () => clearTimeout(t);
    }
  }, [loading, lead, autoprint, id, qrReady, coverDone]);
  useEffect(() => {
    if (!autoprint) return;
    const h = () => window.close();
    window.addEventListener("afterprint", h);
    return () => window.removeEventListener("afterprint", h);
  }, [autoprint]);

  const propertyParam = searchParams.get("property");

  useEffect(() => {
    if (!id) return;
    const fetchLead = async () => {
      setLoading(true);
      setError(null);
      try {
        const url = propertyParam
          ? `${API}/pdf/leads/${id}?property=${encodeURIComponent(propertyParam)}`
          : `${API}/pdf/leads/${id}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = await res.json();
        setLead(data);
      } catch (err) {
        console.error("Failed to fetch lead:", err);
        setError("Could not load lead details.");
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [id, propertyParam]);

  const handlePrint = () => {
    if (onPrint) return onPrint();
    window.print();
  };
  const handleClose = () => {
    if (onClose) return onClose();
    navigate(-1);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#e7e6e2",
          fontFamily: "Arial, Helvetica, sans-serif",
        }}
      >
        Loading lead details…
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: SP.md,
          backgroundColor: "#e7e6e2",
          fontFamily: "Arial, Helvetica, sans-serif",
        }}
      >
        <p>{error || "Lead not found."}</p>
        <button
          onClick={handleClose}
          style={{
            padding: `${SP.xs}px ${SP.lg}px`,
            backgroundColor: "#1E2827",
            color: "#e7e6e2",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          ← Go Back
        </button>
      </div>
    );
  }

  // ── Build properties (logic unchanged) ──
  const propertyDetails = lead.propertydetails || {};
  const propertyNames = Object.keys(propertyDetails).length
    ? Object.keys(propertyDetails)
    : lead.propertyname || lead.property
      ? [lead.propertyname || lead.property]
      : [];

  const properties = propertyNames.map((name) => {
    const pd = propertyDetails[name] || {};
    return {
      name,
      destination: pd.destination || lead.destination || "",
      guests: pd.guests || lead.guests || "",
      bedrooms: pd.bedrooms || lead.bedrooms || "",
      inclusions: pd.inclusions || lead.inclusions || "",
      checkin: pd.checkin || lead.checkin || "",
      checkout: pd.checkout || lead.checkout || "",
      checkin_time: pd.checkin_time || lead.checkin_time || "",
      checkout_time: pd.checkout_time || lead.checkout_time || "",
      proprice: pd.proprice || lead.proprice || "",
      secdep: pd.secdep || lead.secdep || "",
      advpay: pd.advpay || lead.advpay || "",
      balpay: pd.balpay || lead.balpay || "",
      totpay: pd.totpay || lead.totpay || "",
    };
  });

  // ── Calculate Dynamic Percentages (Advance vs Total, No Rounding) ──
  let advancePercentText = "60"; // default fallback
  let remainingPercentText = "40"; // default fallback

  if (properties.length > 0) {
    const firstProp = properties[0];
    const advanceAmount = parseAmount(firstProp.advpay);
    const balanceAmount = parseAmount(firstProp.balpay);
    let totalAmount = parseAmount(firstProp.totpay);

    // Fallback: if total is missing, calculate it using advance + balance
    if (!totalAmount) {
      totalAmount = advanceAmount + balanceAmount;
    }

    if (totalAmount > 0 && advanceAmount > 0) {
      // Calculate exact percentages without rounding off the whole number
      const advPctRaw = (advanceAmount / totalAmount) * 100;
      const remPctRaw = 100 - advPctRaw;

      // Format to a maximum of 2 decimal places to avoid long repeating numbers
      // e.g., 11.11 and 88.89
      advancePercentText = parseFloat(advPctRaw.toFixed(2)).toString();
      remainingPercentText = parseFloat(remPctRaw.toFixed(2)).toString();
    }
  }

  // ── Styles ──
  const pageStyle = {
    margin: 0,
    padding: 0,
    backgroundColor: "#e7e6e2",
    fontFamily: "Arial, Helvetica, sans-serif",
    minHeight: "100vh",
  };

  const cardStyle = {
    maxWidth: 860,
    margin: "0 auto",
    backgroundColor: "#e7e6e2",
    padding: `${SP.xxl}px ${SP.xl}px ${SP.xxl}px`,
    color: "#111",
    boxSizing: "border-box",
  };

  return (
    <>
      <style>{`
  @page {
    size: A4;
    margin: 0;
  }

  .first-page {
    display: flex;
    flex-direction: column;
  }

  /* screen only: gap between pages */
  .print-page {
    margin-bottom: 32px;
  }

  @media print {
    html,
    body {
      margin: 0 !important;
      padding: 0 !important;
      background: #e7e6e2 !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .no-print {
      display: none !important;
    }

    .pdf-card {
      width: 100% !important;
      max-width: none !important;
      margin: 0 !important;
      padding: 0 !important;
      background: transparent !important;
    }

    .print-page {
      box-sizing: border-box;
      width: 210mm;
      height: 296mm;
      padding: 18mm 16mm;
      margin: 0;
      overflow: hidden;
      background: #e7e6e2 !important;
      page-break-after: always;
      break-after: page;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    .print-page:last-child {
      page-break-after: auto;
      break-after: auto;
    }
  }
`}</style>

      <div style={pageStyle}>
        {/* Action bar */}
        <div
          className="no-print"
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            backgroundColor: "#1E2827",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: `${SP.sm}px ${SP.lg}px`,
            gap: SP.sm,
          }}
        >
          <span style={{ color: "#e7e6e2", fontWeight: "bold", fontSize: 15 }}>
            Pre-Booking Document — {lead.firstname} {lead.lastname}
          </span>
          <div style={{ display: "flex", gap: SP.xs }}>
            <button
              onClick={handlePrint}
              style={{
                padding: `${SP.xs}px ${SP.lg}px`,
                backgroundColor: "#e7e6e2",
                color: "#1E2827",
                border: "none",
                borderRadius: 6,
                fontWeight: "bold",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              🖨 Print / Save PDF
            </button>
            <button
              onClick={handleClose}
              style={{
                padding: `${SP.xs}px ${SP.md}px`,
                backgroundColor: "transparent",
                color: "#e7e6e2",
                border: "1px solid #e7e6e2",
                borderRadius: 6,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Document Body */}
        <div className="pdf-card" style={cardStyle}>
          {/* ───────── PAGE 1 ───────── */}
          <div className="print-page first-page">
            {/* Header: Dear + Thank you on the left, logo on the right */}
            <table
              width="100%"
              cellPadding={0}
              cellSpacing={0}
              style={{ marginBottom: SP.sm }}
            >
              <tbody>
                <tr>
                  <td style={{ width: "80%", verticalAlign: "middle" }}>
                    <p
                      style={{
                        fontSize: 20,
                        lineHeight: 1.6,
                        margin: `0 0 ${SP.md}px 0`,
                        color: "#111",
                      }}
                    >
                      Dear{" "}
                      <strong>
                        {lead.firstname} {lead.lastname}
                      </strong>
                      ,
                    </p>
                    <p
                      style={{
                        fontSize: 17,
                        lineHeight: 1.6,
                        margin: 0,
                        color: "#111",
                      }}
                    >
                      Thank you for considering Zorba Stays for your upcoming
                      visit.
                    </p>
                  </td>
                  <td
                    style={{
                      width: "20%",
                      textAlign: "right",
                      verticalAlign: "middle",
                    }}
                  >
                    <img
                      src="https://zorbastays.com/android-chrome-512x512.png"
                      alt="Zorba Stays"
                      width={90}
                      height={90}
                      style={{
                        display: "inline-block",
                        borderRadius: "50%",
                        objectFit: "cover",
                      }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Fixed-size, centered cover image */}
            <CoverImage
              src={lead.cover_image}
              fallback={lead.cover_fallback}
              onDone={() => setCoverDone(true)}
            />

            {/* Intro text */}
            <div>
              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.6,
                  margin: `${SP.lg}px 0 ${SP.lg}px 0`,
                  color: "#111",
                }}
              >
                We are pleased to share the details for your requested stay:
              </p>

              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.6,
                  margin: `0 0 ${SP.lg}px 0`,
                  color: "#111",
                }}
              >
                To confirm your reservation, we require a{" "}
                <strong>{advancePercentText}%</strong> advance payment; the{" "}
                <strong>remaining {remainingPercentText}%</strong> + the
                security deposit (if applicable) is to be completed before
                check-in.
              </p>

              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.6,
                  margin: `0 0 ${SP.lg}px 0`,
                  color: "#111",
                }}
              >
                As villa availability remains dynamic, we are unable to block or
                guarantee dates without payment confirmation. We therefore
                recommend completing the advance payment at the earliest should
                you wish to secure the property.
              </p>

              <p
                style={{
                  fontSize: 16,
                  lineHeight: 1.6,
                  margin: 0,
                  color: "#111",
                }}
              >
                Kindly review the stay details, payment terms, cancellation
                policy, and house rules attached below. We would be delighted to
                host you and assist with any special arrangements for your stay.
              </p>
            </div>

            {/* Page number pinned to the bottom of page 1
  <div className="page-number">Page 1</div> */}
          </div>
          {/* Properties — each one on its own page, fully glued together */}
          {properties.map((p, idx) => {
            const nights = calcNights(p.checkin, p.checkout);

            // First property: no page-break-before (flows after intro).
            // Subsequent properties: force a new page.

            return (
              <div key={idx} className="print-page">
                {/* ONE avoid-break block wraps title + both tables + total,
                      so nothing inside splits to a new page. */}
                <div className="avoid-break">
                  <SectionTitle>STAY DETAILS — {p.name}</SectionTitle>
                  <table
                    width="100%"
                    cellPadding={0}
                    cellSpacing={0}
                    style={{
                      marginBottom: SP.xxl,
                      borderCollapse: "collapse",
                      backgroundColor: "#e7e6e2",
                    }}
                  >
                    <tbody>
                      <DetailRow label="PROPERTY:" value={p.name || "N/A"} />
                      <DetailRow
                        label="LOCATION:"
                        value={p.destination || "N/A"}
                      />
                      <DetailRow
                        label="CHECK-IN:"
                        value={p.checkin ? formatDate(p.checkin) : "N/A"}
                      />
                      <DetailRow
                        label="CHECK-OUT:"
                        value={p.checkout ? formatDate(p.checkout) : "N/A"}
                      />
                      <DetailRow
                        label="CHECK-IN TIME:"
                        value={p.checkin_time || "N/A"}
                      />
                      <DetailRow
                        label="CHECK-OUT TIME:"
                        value={p.checkout_time || "N/A"}
                      />
                      <DetailRow
                        label="NUMBER OF GUESTS:"
                        value={p.guests || "N/A"}
                      />
                      <DetailRow
                        label="BEDROOMS:"
                        value={p.bedrooms || "N/A"}
                      />
                      <DetailRow
                        label="INCLUSION:"
                        value={p.inclusions || "N/A"}
                      />
                    </tbody>
                  </table>

                  <SectionTitle>STAY PRICING — {p.name}</SectionTitle>
                  <table
                    width="100%"
                    cellPadding={0}
                    cellSpacing={0}
                    style={{
                      fontSize: 14,
                      marginBottom: SP.lg,
                      borderCollapse: "collapse",
                    }}
                  >
                    <tbody>
                      <DetailRow
                        label="NIGHTLY RATE:"
                        value={`₹${p.proprice || "N/A"}`}
                      />
                      <DetailRow
                        label="NUMBER OF NIGHTS:"
                        value={`${nights} Night${nights !== 1 ? "s" : ""}`}
                      />
                      <tr>
                        <td
                          style={{
                            padding: `${SP.xs}px 0`,
                            fontSize: 14,
                            verticalAlign: "top",
                          }}
                        >
                          REFUNDABLE SECURITY DEPOSIT:
                        </td>
                        <td
                          style={{
                            padding: `${SP.xs}px 0`,
                            fontSize: 14,
                            textAlign: "right",
                            verticalAlign: "top",
                          }}
                        >
                          ₹{p.secdep || "0.00"}
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan={2}
                          style={{
                            padding: `0 0 ${SP.xs}px 0`,
                            fontSize: 12,
                            color: "#555",
                            verticalAlign: "top",
                          }}
                        >
                          (To be paid at check-in &amp; fully refundable at
                          check-out, subject to any damage to the property)
                        </td>
                      </tr>
                      <DetailRow
                        label="ADVANCE AMOUNT:"
                        value={`₹${p.advpay || "N/A"}`}
                      />
                      <DetailRow
                        label="BALANCE DUE:"
                        value={`₹${p.balpay || "N/A"}`}
                      />
                    </tbody>
                  </table>

                  <p
                    style={{
                      fontSize: 16,
                      fontWeight: "bold",
                      textAlign: "right",
                      margin: `0 0 ${SP.xxl}px 0`,
                    }}
                  >
                    TOTAL PAYABLE: ₹{p.totpay || "N/A"}
                  </p>
                </div>
              </div>
            );
          })}
          <div className="print-page">
            {/* Cancellation policy */}
            <div>
              <SectionTitle>CANCELLATION POLICY</SectionTitle>
              <ul
                style={{
                  fontSize: 15,
                  margin: `0 0 ${SP.xxl}px ${SP.lg}px`,
                  padding: 0,
                  lineHeight: 1.7,
                }}
              >
                <li style={{ marginBottom: SP.md }}>
                  <span className="text-xl mr-2">•</span> Advance would be
                  refundable if the booking is cancelled 30 days before the
                  arrival date. The advance would not be refundable if the
                  booking is cancelled within the 30-day period from the arrival
                  date.
                </li>
                <li>
                  <span className="text-xl mr-2">•</span> In case of any
                  lockdown or government restrictions on travel, the initial
                  payment would be fully refundable. Otherwise, refunds are not
                  available unless we manage to resell the dates.
                </li>
              </ul>
            </div>

            {/* Payment info */}
            <div>
              <Divider />
              <table
                width="100%"
                cellPadding={0}
                cellSpacing={0}
                style={{
                  borderCollapse: "collapse",
                  marginTop: SP.md,
                  tableLayout: "fixed",
                }}
              >
                <tbody>
                  <tr>
                    <td
                      style={{
                        width: "60%",
                        verticalAlign: "top",
                        paddingRight: SP.lg,
                      }}
                    >
                      <SectionTitle>PAYMENT INFO</SectionTitle>
                      {[
                        ["Bank", "HDFC Bank"],
                        ["A/C No.", "50200077506738"],
                        ["A/C Name", "ZORBA STAYS"],
                        ["IFSC", "HDFC0000101"],
                        ["Branch", "RAJKOT - YAGNIK ROAD - GUJARAT"],
                        ["Account Type", "CURRENT"],
                      ].map(([label, val]) => (
                        <p
                          key={label}
                          style={{
                            margin: `0 0 ${SP.sm}px 0`,
                            fontSize: 14,
                            color: "#111",
                          }}
                        >
                          <strong>{label}:</strong> {val}
                        </p>
                      ))}
                    </td>
                    <td
                      style={{
                        width: "40%",
                        verticalAlign: "top",
                        textAlign: "right",
                      }}
                    >
                      <SectionTitle>SCAN &amp; PAY</SectionTitle>
                      <img
                        src="https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public"
                        alt="QR Code"
                        style={{
                          width: 180,
                          height: 180,
                          border: "1px solid #ddd",
                          display: "inline-block",
                        }}
                      />
                      <p
                        style={{
                          margin: `${SP.sm}px 0 ${SP.xs}px 0`,
                          fontSize: 14,
                        }}
                      >
                        <strong>Name:</strong> ZORBA STAYS
                      </p>
                      <p style={{ margin: 0, fontSize: 14 }}>
                        <strong>UPI ID:</strong> 8866262368@hdfcbank
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Closing */}
            <p style={{ fontSize: 15, margin: `${SP.xxl}px 0 ${SP.xl}px 0` }}>
              Warm regards,
              <br />
              <strong>Team Zorba Stays</strong>
            </p>

            {/* Footer nav */}
            <div style={{ marginBottom: SP.md, textAlign: "center" }}>
              {[
                "Contact Us",
                "Experiences",
                "Destinations",
                "Travel Stories",
              ].map((link, i) => (
                <a
                  key={i}
                  href="#"
                  style={{
                    display: "inline-block",
                    marginRight: SP.xl,
                    fontSize: 12,
                    color: "#000",
                    textDecoration: "none",
                  }}
                >
                  {link}
                </a>
              ))}
            </div>
            <p
              style={{
                fontSize: 12,
                color: "#777",
                lineHeight: 1.6,
                margin: 0,
              }}
            >
              Zorba Stays is registered in India, Registered company number:
              90167 72761, Registered address: 308 - Business Centrum, Amin
              Marg, Rajkot, India
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default PreLeadPDF;
//============================================

// import React, { useEffect, useState } from "react";
// import { useParams, useNavigate, useSearchParams } from "react-router-dom";

// const API = `${process.env.REACT_APP_BACKEND_URL || "https://dash.zorbastays.com"}/web-backend`;

// // ─── Helpers ────────────────────────────────────────────────────────────────
// const formatDate = (dateStr) => {
//   if (!dateStr) return "N/A";
//   const d = new Date(dateStr);
//   if (isNaN(d)) return dateStr;
//   return d.toLocaleDateString("en-GB", {
//     day: "2-digit",
//     month: "long",
//     year: "numeric",
//   });
// };

// const calcNights = (checkin, checkout) => {
//   if (!checkin || !checkout) return 0;
//   const diff = new Date(checkout) - new Date(checkin);
//   return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
// };

// // Helper to parse currency strings safely
// const parseAmount = (val) => {
//   if (!val) return 0;
//   const num = parseFloat(String(val).replace(/,/g, ""));
//   return isNaN(num) ? 0 : num;
// };

// const SP = { xs: 8, sm: 12, md: 16, lg: 24, xl: 32, xxl: 40 };

// // ─── Sub-components ─────────────────────────────────────────────────────────

// const Divider = () => (
//   <hr
//     style={{
//       height: 3,
//       border: "none",
//       backgroundColor: "#1E2827",
//       margin: `${SP.xl}px 0`,
//     }}
//   />
// );

// const SectionTitle = ({ children }) => (
//   <h2
//     style={{
//       fontSize: 18,
//       fontWeight: "bold",
//       margin: `0 0 ${SP.md}px 0`,
//       letterSpacing: 1,
//       color: "#111",
//     }}
//   >
//     {children}
//   </h2>
// );

// // Return an array of <tr>s — safest pattern inside <tbody> during print.
// const DetailRow = ({ label, value, subText }) => {
//   const rows = [
//     <tr key="main">
//       <td
//         style={{
//           padding: `${SP.xs}px 0`,
//           fontWeight: "bold",
//           width: "50%",
//           fontSize: 14,
//           color: "#111",
//           verticalAlign: "top",
//         }}
//       >
//         {label}
//       </td>
//       <td
//         style={{
//           padding: `${SP.xs}px 0`,
//           width: "50%",
//           textAlign: "right",
//           fontSize: 14,
//           color: "#111",
//           verticalAlign: "top",
//         }}
//       >
//         {value}
//       </td>
//     </tr>,
//   ];
//   if (subText) {
//     rows.push(
//       <tr key="sub">
//         <td
//           colSpan={2}
//           style={{
//             padding: `0 0 ${SP.xs}px 0`,
//             fontSize: 12,
//             color: "#555",
//             verticalAlign: "top",
//           }}
//         >
//           {subText}
//         </td>
//       </tr>,
//     );
//   }
//   return rows;
// };
// const CoverImage = ({ src, fallback, onDone }) => {
//   const [current, setCurrent] = useState(src || fallback || null);
//   const [usedFallback, setUsedFallback] = useState(!src);

//   useEffect(() => {
//     setCurrent(src || fallback || null);
//     setUsedFallback(!src);
//     if (!src && !fallback) onDone?.();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [src, fallback]);

//   if (!current) return null;

//   return (
//     <img
//       src={current}
//       alt=""
//       onLoad={() => onDone?.()}
//       onError={() => {
//         if (!usedFallback && fallback && fallback !== current) {
//           setCurrent(fallback);
//           setUsedFallback(true);
//         } else {
//           setCurrent(null); // both failed: hide it, never show a broken icon
//           onDone?.();
//         }
//       }}
//       style={{
//         display: "block",
//         width: "100%",
//         height: "50%",
//         objectFit: "cover",
//         borderRadius: 8,
//         margin: `0 0 ${SP.lg}px 0`,
//       }}
//     />
//   );
// };
// // ─── Main Component ─────────────────────────────────────────────────────────

// const PreLeadPDF = ({ onClose, onPrint }) => {
//   const { id } = useParams();
//   const navigate = useNavigate();

//   const [lead, setLead] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [searchParams] = useSearchParams();
//   const autoprint = searchParams.get("autoprint") === "1";
//   const [qrReady, setQrReady] = useState(false);
//   const [coverDone, setCoverDone] = useState(false);
//   useEffect(() => {
//     const img = new Image();
//     img.onload = () => setQrReady(true);
//     img.onerror = () => setQrReady(true);
//     img.src =
//       "https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public";
//   }, []);

//   useEffect(() => {
//     if (lead && !lead.cover_image && !lead.cover_fallback) setCoverDone(true);
//   }, [lead]);
//   useEffect(() => {
//     if (!loading && lead && autoprint && qrReady && coverDone) {
//       const t = setTimeout(() => {
//         const firstname = (lead.firstname || lead.customer_name || "Lead")
//           .trim()
//           .replace(/\s+/g, "_");
//         const leadId = lead.id || id;
//         document.title = `${firstname}_${leadId}_prebooking.pdf`;
//         window.print();
//       }, 800);
//       return () => clearTimeout(t);
//     }
//   }, [loading, lead, autoprint, id, qrReady, coverDone]);
//   useEffect(() => {
//     if (!autoprint) return;
//     const h = () => window.close();
//     window.addEventListener("afterprint", h);
//     return () => window.removeEventListener("afterprint", h);
//   }, [autoprint]);

//   const propertyParam = searchParams.get("property");

//   useEffect(() => {
//     if (!id) return;
//     const fetchLead = async () => {
//       setLoading(true);
//       setError(null);
//       try {
//         const url = propertyParam
//           ? `${API}/pdf/leads/${id}?property=${encodeURIComponent(propertyParam)}`
//           : `${API}/pdf/leads/${id}`;
//         const res = await fetch(url);
//         if (!res.ok) throw new Error(`Request failed: ${res.status}`);
//         const data = await res.json();
//         setLead(data);
//       } catch (err) {
//         console.error("Failed to fetch lead:", err);
//         setError("Could not load lead details.");
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchLead();
//   }, [id, propertyParam]);

//   const handlePrint = () => {
//     if (onPrint) return onPrint();
//     window.print();
//   };
//   const handleClose = () => {
//     if (onClose) return onClose();
//     navigate(-1);
//   };

//   if (loading) {
//     return (
//       <div
//         style={{
//           minHeight: "100vh",
//           display: "flex",
//           alignItems: "center",
//           justifyContent: "center",
//           backgroundColor: "#e7e6e2",
//           fontFamily: "Arial, Helvetica, sans-serif",
//         }}
//       >
//         Loading lead details…
//       </div>
//     );
//   }

//   if (error || !lead) {
//     return (
//       <div
//         style={{
//           minHeight: "100vh",
//           display: "flex",
//           flexDirection: "column",
//           alignItems: "center",
//           justifyContent: "center",
//           gap: SP.md,
//           backgroundColor: "#e7e6e2",
//           fontFamily: "Arial, Helvetica, sans-serif",
//         }}
//       >
//         <p>{error || "Lead not found."}</p>
//         <button
//           onClick={handleClose}
//           style={{
//             padding: `${SP.xs}px ${SP.lg}px`,
//             backgroundColor: "#1E2827",
//             color: "#e7e6e2",
//             border: "none",
//             borderRadius: 6,
//             cursor: "pointer",
//           }}
//         >
//           ← Go Back
//         </button>
//       </div>
//     );
//   }

//   // ── Build properties (logic unchanged) ──
//   const propertyDetails = lead.propertydetails || {};
//   const propertyNames = Object.keys(propertyDetails).length
//     ? Object.keys(propertyDetails)
//     : lead.propertyname || lead.property
//       ? [lead.propertyname || lead.property]
//       : [];

//   const properties = propertyNames.map((name) => {
//     const pd = propertyDetails[name] || {};
//     return {
//       name,
//       destination: pd.destination || lead.destination || "",
//       guests: pd.guests || lead.guests || "",
//       bedrooms: pd.bedrooms || lead.bedrooms || "",
//       inclusions: pd.inclusions || lead.inclusions || "",
//       checkin: pd.checkin || lead.checkin || "",
//       checkout: pd.checkout || lead.checkout || "",
//       checkin_time: pd.checkin_time || lead.checkin_time || "",
//       checkout_time: pd.checkout_time || lead.checkout_time || "",
//       proprice: pd.proprice || lead.proprice || "",
//       secdep: pd.secdep || lead.secdep || "",
//       advpay: pd.advpay || lead.advpay || "",
//       balpay: pd.balpay || lead.balpay || "",
//       totpay: pd.totpay || lead.totpay || "",
//     };
//   });

//   // ── Calculate Dynamic Percentages (Advance vs Total, No Rounding) ──
//   let advancePercentText = "60"; // default fallback
//   let remainingPercentText = "40"; // default fallback

//   if (properties.length > 0) {
//     const firstProp = properties[0];
//     const advanceAmount = parseAmount(firstProp.advpay);
//     const balanceAmount = parseAmount(firstProp.balpay);
//     let totalAmount = parseAmount(firstProp.totpay);

//     // Fallback: if total is missing, calculate it using advance + balance
//     if (!totalAmount) {
//       totalAmount = advanceAmount + balanceAmount;
//     }

//     if (totalAmount > 0 && advanceAmount > 0) {
//       // Calculate exact percentages without rounding off the whole number
//       const advPctRaw = (advanceAmount / totalAmount) * 100;
//       const remPctRaw = 100 - advPctRaw;

//       // Format to a maximum of 2 decimal places to avoid long repeating numbers
//       // e.g., 11.11 and 88.89
//       advancePercentText = parseFloat(advPctRaw.toFixed(2)).toString();
//       remainingPercentText = parseFloat(remPctRaw.toFixed(2)).toString();
//     }
//   }

//   // ── Styles ──
//   const pageStyle = {
//     margin: 0,
//     padding: 0,
//     backgroundColor: "#e7e6e2",
//     fontFamily: "Arial, Helvetica, sans-serif",
//     minHeight: "100vh",
//   };

//   const cardStyle = {
//     maxWidth: 860,
//     margin: "0 auto",
//     backgroundColor: "#e7e6e2",
//     padding: `${SP.xxl}px ${SP.xl}px ${SP.xxl}px`,
//     color: "#111",
//     boxSizing: "border-box",
//   };

//   return (
//     <>
//       <style>{`
//   @page {
//     size: A4;
//     margin: 0;
//   }

//   @media print {
//     html,
//     body {
//       margin: 0 !important;
//       padding: 0 !important;
//       width: 100% !important;
//       background: #e7e6e2 !important;

//       -webkit-print-color-adjust: exact !important;
//       print-color-adjust: exact !important;
//     }

//     .no-print {
//       display: none !important;
//     }

//     .pdf-card {
//       width: 100% !important;
//       max-width: none !important;
//       margin: 0 !important;
//       padding: 18mm 16mm 18mm 16mm !important;
//       box-sizing: border-box !important;

//       background: #e7e6e2 !important;

//       -webkit-print-color-adjust: exact !important;
//       print-color-adjust: exact !important;
//     }

//     .page-break-before,
//     .page-top-pad {
//       padding-top: 18mm !important;
//       box-sizing: border-box !important;
//     }

//     .page-break-before {
//       page-break-before: always;
//       break-before: page;
//     }

//     .avoid-break {
//       page-break-inside: avoid;
//       break-inside: avoid;
//     }

//     .keep-with-table {
//       page-break-after: avoid;
//       break-after: avoid;
//     }
//   }
// `}</style>

//       <div style={pageStyle}>
//         {/* Action bar */}
//         <div
//           className="no-print"
//           style={{
//             position: "sticky",
//             top: 0,
//             zIndex: 100,
//             backgroundColor: "#1E2827",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "space-between",
//             padding: `${SP.sm}px ${SP.lg}px`,
//             gap: SP.sm,
//           }}
//         >
//           <span style={{ color: "#e7e6e2", fontWeight: "bold", fontSize: 15 }}>
//             Pre-Booking Document — {lead.firstname} {lead.lastname}
//           </span>
//           <div style={{ display: "flex", gap: SP.xs }}>
//             <button
//               onClick={handlePrint}
//               style={{
//                 padding: `${SP.xs}px ${SP.lg}px`,
//                 backgroundColor: "#e7e6e2",
//                 color: "#1E2827",
//                 border: "none",
//                 borderRadius: 6,
//                 fontWeight: "bold",
//                 fontSize: 13,
//                 cursor: "pointer",
//               }}
//             >
//               🖨 Print / Save PDF
//             </button>
//             <button
//               onClick={handleClose}
//               style={{
//                 padding: `${SP.xs}px ${SP.md}px`,
//                 backgroundColor: "transparent",
//                 color: "#e7e6e2",
//                 border: "1px solid #e7e6e2",
//                 borderRadius: 6,
//                 fontSize: 13,
//                 cursor: "pointer",
//               }}
//             >
//               ✕ Close
//             </button>
//           </div>
//         </div>

//         {/* Document Body */}
//         <div className="pdf-card" style={cardStyle}>
//           {/* Header row: greeting + small logo */}
//           <table
//             width="100%"
//             cellPadding={0}
//             cellSpacing={0}
//             style={{ marginBottom: SP.md }}
//           >
//             <tbody>
//               <tr>
//                 <td style={{ width: "80%", verticalAlign: "middle" }}>
//                   <p
//                     style={{
//                       fontSize: 20,
//                       lineHeight: 1.6,
//                       margin: 0,
//                       color: "#111",
//                     }}
//                   >
//                     Dear{" "}
//                     <strong>
//                       {lead.firstname} {lead.lastname}
//                     </strong>
//                     ,
//                   </p>
//                 </td>
//                 <td
//                   style={{
//                     width: "20%",
//                     textAlign: "right",
//                     verticalAlign: "middle",
//                   }}
//                 >
//                   <img
//                     src="https://zorbastays.com/android-chrome-512x512.png"
//                     alt="Zorba Stays"
//                     width={80}
//                     style={{ display: "inline-block" }}
//                   />
//                 </td>
//               </tr>
//             </tbody>
//           </table>

//           <CoverImage
//             src={lead.cover_image}
//             fallback={lead.cover_fallback}
//             onDone={() => setCoverDone(true)}
//           />
//           <p
//             style={{
//               fontSize: 17,
//               lineHeight: 1.6,
//               margin: `0 0 ${SP.md}px 0`,
//               color: "#111",
//             }}
//           >
//             Thank you for considering Zorba Stays for your upcoming visit.
//           </p>
//           {/* Intro — taller paragraphs, bigger line-height, more space between */}
//           <div className="avoid-break">
//             <p
//               style={{
//                 fontSize: 17,
//                 lineHeight: 1.75,
//                 margin: `${SP.xxl}px 0 ${SP.xl}px 0`,
//               }}
//             >
//               We are pleased to share the details for your requested stay:
//             </p>

//             <p
//               style={{
//                 fontSize: 17,
//                 lineHeight: 1.75,
//                 margin: `0 0 ${SP.xl}px 0`,
//                 maxWidth: 720,
//               }}
//             >
//               To confirm your reservation, we require a{" "}
//               <strong>{advancePercentText}%</strong> advance payment; the{" "}
//               <strong>remaining {remainingPercentText}%</strong> + the security
//               deposit (if applicable) is to be completed before check-in.
//             </p>

//             <p
//               style={{
//                 fontSize: 17,
//                 lineHeight: 1.75,
//                 margin: `0 0 ${SP.xl}px 0`,
//               }}
//             >
//               As villa availability remains dynamic, we are unable to block or
//               guarantee dates without payment confirmation. We therefore
//               recommend completing the advance payment at the earliest should
//               you wish to secure the property.
//             </p>

//             <p
//               style={{
//                 fontSize: 17,
//                 lineHeight: 1.75,
//                 margin: `0 0 ${SP.xxl}px 0`,
//               }}
//             >
//               Kindly review the stay details, payment terms, cancellation
//               policy, and house rules attached below. We would be delighted to
//               host you and assist with any special arrangements for your stay.
//             </p>
//           </div>

//           {/* Properties — each one on its own page, fully glued together */}
//           {properties.map((p, idx) => {
//             const nights = calcNights(p.checkin, p.checkout);

//             // First property: no page-break-before (flows after intro).
//             // Subsequent properties: force a new page.
//             const wrapperClass = "page-break-before";

//             return (
//               <div key={idx} className={wrapperClass}>
//                 {/* ONE avoid-break block wraps title + both tables + total,
//                       so nothing inside splits to a new page. */}
//                 <div className="avoid-break">
//                   <SectionTitle>STAY DETAILS — {p.name}</SectionTitle>
//                   <table
//                     width="100%"
//                     cellPadding={0}
//                     cellSpacing={0}
//                     style={{
//                       marginBottom: SP.xxl,
//                       borderCollapse: "collapse",
//                       backgroundColor: "#e7e6e2",
//                     }}
//                   >
//                     <tbody>
//                       <DetailRow label="PROPERTY:" value={p.name || "N/A"} />
//                       <DetailRow
//                         label="LOCATION:"
//                         value={p.destination || "N/A"}
//                       />
//                       <DetailRow
//                         label="CHECK-IN:"
//                         value={p.checkin ? formatDate(p.checkin) : "N/A"}
//                       />
//                       <DetailRow
//                         label="CHECK-OUT:"
//                         value={p.checkout ? formatDate(p.checkout) : "N/A"}
//                       />
//                       <DetailRow
//                         label="CHECK-IN TIME:"
//                         value={p.checkin_time || "N/A"}
//                       />
//                       <DetailRow
//                         label="CHECK-OUT TIME:"
//                         value={p.checkout_time || "N/A"}
//                       />
//                       <DetailRow
//                         label="NUMBER OF GUESTS:"
//                         value={p.guests || "N/A"}
//                       />
//                       <DetailRow
//                         label="BEDROOMS:"
//                         value={p.bedrooms || "N/A"}
//                       />
//                       <DetailRow
//                         label="INCLUSION:"
//                         value={p.inclusions || "N/A"}
//                       />
//                     </tbody>
//                   </table>

//                   <SectionTitle>STAY PRICING — {p.name}</SectionTitle>
//                   <table
//                     width="100%"
//                     cellPadding={0}
//                     cellSpacing={0}
//                     style={{
//                       fontSize: 14,
//                       marginBottom: SP.lg,
//                       borderCollapse: "collapse",
//                     }}
//                   >
//                     <tbody>
//                       <DetailRow
//                         label="NIGHTLY RATE:"
//                         value={`₹${p.proprice || "N/A"}`}
//                       />
//                       <DetailRow
//                         label="NUMBER OF NIGHTS:"
//                         value={`${nights} Night${nights !== 1 ? "s" : ""}`}
//                       />
//                       <tr>
//                         <td
//                           style={{
//                             padding: `${SP.xs}px 0`,
//                             fontSize: 14,
//                             verticalAlign: "top",
//                           }}
//                         >
//                           REFUNDABLE SECURITY DEPOSIT:
//                         </td>
//                         <td
//                           style={{
//                             padding: `${SP.xs}px 0`,
//                             fontSize: 14,
//                             textAlign: "right",
//                             verticalAlign: "top",
//                           }}
//                         >
//                           ₹{p.secdep || "0.00"}
//                         </td>
//                       </tr>
//                       <tr>
//                         <td
//                           colSpan={2}
//                           style={{
//                             padding: `0 0 ${SP.xs}px 0`,
//                             fontSize: 12,
//                             color: "#555",
//                             verticalAlign: "top",
//                           }}
//                         >
//                           (To be paid at check-in &amp; fully refundable at
//                           check-out, subject to any damage to the property)
//                         </td>
//                       </tr>
//                       <DetailRow
//                         label="ADVANCE AMOUNT:"
//                         value={`₹${p.advpay || "N/A"}`}
//                       />
//                       <DetailRow
//                         label="BALANCE DUE:"
//                         value={`₹${p.balpay || "N/A"}`}
//                       />
//                     </tbody>
//                   </table>

//                   <p
//                     style={{
//                       fontSize: 16,
//                       fontWeight: "bold",
//                       textAlign: "right",
//                       margin: `0 0 ${SP.xxl}px 0`,
//                     }}
//                   >
//                     TOTAL PAYABLE: ₹{p.totpay || "N/A"}
//                   </p>
//                 </div>
//               </div>
//             );
//           })}
//           <div className="page-break-before">
//             {/* Cancellation policy */}
//             <div className="avoid-break ">
//               <SectionTitle>CANCELLATION POLICY</SectionTitle>
//               <ul
//                 style={{
//                   fontSize: 15,
//                   margin: `0 0 ${SP.xxl}px ${SP.lg}px`,
//                   padding: 0,
//                   lineHeight: 1.7,
//                 }}
//               >
//                 <li style={{ marginBottom: SP.md }}>
//                   <span className="text-xl mr-2">•</span> Advance would be
//                   refundable if the booking is cancelled 30 days before the
//                   arrival date. The advance would not be refundable if the
//                   booking is cancelled within the 30-day period from the arrival
//                   date.
//                 </li>
//                 <li>
//                   <span className="text-xl mr-2">•</span> In case of any
//                   lockdown or government restrictions on travel, the initial
//                   payment would be fully refundable. Otherwise, refunds are not
//                   available unless we manage to resell the dates.
//                 </li>
//               </ul>
//             </div>

//             {/* Payment info — flows naturally after cancellation policy.
//                 No page-break-before, so no Chrome glyph duplication. */}
//             <div className="avoid-break ">
//               <Divider />
//               <table
//                 width="100%"
//                 cellPadding={0}
//                 cellSpacing={0}
//                 style={{
//                   borderCollapse: "collapse",
//                   marginTop: SP.md,
//                   tableLayout: "fixed",
//                 }}
//               >
//                 <tbody>
//                   <tr>
//                     <td
//                       style={{
//                         width: "60%",
//                         verticalAlign: "top",
//                         paddingRight: SP.lg,
//                       }}
//                     >
//                       <SectionTitle>PAYMENT INFO</SectionTitle>
//                       {[
//                         ["Bank", "HDFC Bank"],
//                         ["A/C No.", "50200077506738"],
//                         ["A/C Name", "ZORBA STAYS"],
//                         ["IFSC", "HDFC0000101"],
//                         ["Branch", "RAJKOT - YAGNIK ROAD - GUJARAT"],
//                         ["Account Type", "CURRENT"],
//                       ].map(([label, val]) => (
//                         <p
//                           key={label}
//                           style={{
//                             margin: `0 0 ${SP.sm}px 0`,
//                             fontSize: 14,
//                             color: "#111",
//                           }}
//                         >
//                           <strong>{label}:</strong> {val}
//                         </p>
//                       ))}
//                     </td>
//                     <td
//                       style={{
//                         width: "40%",
//                         verticalAlign: "top",
//                         textAlign: "right",
//                       }}
//                     >
//                       <SectionTitle>SCAN &amp; PAY</SectionTitle>
//                       <img
//                         src="https://imagedelivery.net/1KyJN6DqRSgEbKUMt_Oskw/3d7e03f8-1d5f-4377-60bc-161fe96edc00/public"
//                         alt="QR Code"
//                         style={{
//                           width: 180,
//                           height: 180,
//                           border: "1px solid #ddd",
//                           display: "inline-block",
//                         }}
//                       />
//                       <p
//                         style={{
//                           margin: `${SP.sm}px 0 ${SP.xs}px 0`,
//                           fontSize: 14,
//                         }}
//                       >
//                         <strong>Name:</strong> ZORBA STAYS
//                       </p>
//                       <p style={{ margin: 0, fontSize: 14 }}>
//                         <strong>UPI ID:</strong> 8866262368@hdfcbank
//                       </p>
//                     </td>
//                   </tr>
//                 </tbody>
//               </table>
//             </div>
//           </div>
//           {/* Closing */}
//           <p style={{ fontSize: 15, margin: `${SP.xxl}px 0 ${SP.xl}px 0` }}>
//             Warm regards,
//             <br />
//             <strong>Team Zorba Stays</strong>
//           </p>

//           {/* Footer nav */}
//           <div style={{ marginBottom: SP.md, textAlign: "center" }}>
//             {[
//               "Contact Us",
//               "Experiences",
//               "destinations",
//               "Travel Stories",
//             ].map((link, i) => (
//               <a
//                 key={i}
//                 href="#"
//                 style={{
//                   display: "inline-block",
//                   marginRight: SP.xl,
//                   fontSize: 12,
//                   color: "#000",
//                   textDecoration: "none",
//                 }}
//               >
//                 {link}
//               </a>
//             ))}
//           </div>
//           <p
//             style={{
//               fontSize: 12,
//               color: "#777",
//               lineHeight: 1.6,
//               margin: 0,
//             }}
//           >
//             Zorba Stays is registered in India, Registered company number: 90167
//             72761, Registered address: 308 - Business Centrum, Amin Marg,
//             Rajkot, India
//           </p>
//         </div>
//       </div>
//     </>
//   );
// };

// export default PreLeadPDF;
// //============================================
